import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Badge,
  BadgeSize,
  BadgeType,
  Button,
  ButtonColor,
  ButtonSize,
  HighValueProductLabel,
  Icon,
  IconType,
  TooltipInfo,
} from '../../../../components'
import { Avatar } from '../../../../elements'
import {
  Campaign,
  ContentContractType,
  Creator,
  ProductType,
  BrandServiceLevel,
} from '../../../../gql/types'
import {
  modelTypeLocalizedString,
  videoDurationLocalizedString,
  videoTypeLocalizedString,
  voiceTypeLocalizedString,
} from '../../../../utils/GqlStrings'
import {
  mapAddonsToTooltipTextForCreators,
  mapAspectRatioToText,
  mapCurrencyToSign,
} from '../../../../utils/mappers'
import { PricingData, formatPriceInCents } from '../../../../utils/PricingUtil'
import { CampaignSlider } from '../../../components/CampaignSlider'
import { CreatorRoutes } from '../../../CreatorRoutes'
import { ApplyToCampaignModal } from '../ApplyToCampaignModal'
import classNames from 'classnames'

interface IProps {
  creator: Creator
  campaign: Campaign
  invitationId?: number | null
  pricing: PricingData
  onAppliedToCampaign: () => void
}

const CampaignCard = ({
  creator,
  campaign,
  invitationId,
  pricing,
  onAppliedToCampaign,
}: IProps) => {
  const { pathname } = useLocation()
  const [isCampaignSliderOpen, setIsCampaignSliderOpen] = useState(false)
  const [isApplyToCampaignModalOpen, setIsApplyToCampaignModalOpen] =
    useState(false)

  const getTotalCreatorEarningsText = () => {
    return formatPriceInCents({
      priceInCents: campaign.totalCreatorEarningsCents,
      currency: campaign.currency,
    })
  }

  return (
    <>
      <td className='hidden'>
        <CampaignSlider
          open={isCampaignSliderOpen}
          setOpen={setIsCampaignSliderOpen}
          campaign={campaign}
          openApplyToCampaignModal={() => setIsApplyToCampaignModalOpen(true)}
        />
        <ApplyToCampaignModal
          creator={creator}
          campaign={campaign}
          invitationId={invitationId}
          isOpen={isApplyToCampaignModalOpen}
          setIsOpen={setIsApplyToCampaignModalOpen}
          onAppliedToCampaign={onAppliedToCampaign}
        />
      </td>
      <td
        className={classNames(
          'whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6',
          campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove &&
            'border-l-2 border-l-indigo-400 bg-indigo-50/40',
        )}
      >
        <span className='flex items-center'>
          <div
            className='p-1 mr-3 [&>*]:m-0.25 min-w-[50px] cursor-pointer'
            onClick={() => setIsCampaignSliderOpen(true)}
          >
            <Avatar
              type='product'
              src={campaign.product.photo?.url}
              size='sm'
              alt={campaign.product.name}
            />
          </div>

          <div
            onClick={() => setIsCampaignSliderOpen(true)}
            className='cursor-pointer'
          >
            <div>
              {/* <a
                href={campaign.product.link}
                className='text-blue-500'
                target='_blank'
                rel='noopener noreferrer'
              > */}
              <div className='text-ellipsis overflow-hidden w-40 md:w-56'>
                {campaign.product.name}
              </div>
              {/* <div className='mt-4'>purchased at: {moment(campaign.purchasedAt).format('DD-MM-YYYY')}</div> */}
              {/* <div className='mt-4'>boosted at: {campaign.boostedAt ? moment(campaign.boostedAt).format('DD-MM-YYYY') : 'not boosted'}</div> */}
              {/* </a> */}
            </div>

            <div className='truncate mt-1 mb-0 flex flex-shrink-0 text-gray-500'>
              {campaign.product.type === ProductType.digital && (
                <span className='mr-1'>digital product</span>
              )}
              {campaign.product.price > 0 &&
                campaign.product.type === ProductType.physical && (
                  <span className='mr-1'>
                    worth {mapCurrencyToSign(campaign.product.currency)}{' '}
                    {campaign.product.price}
                  </span>
                )}
              <HighValueProductLabel
                isHighValueProduct={campaign?.isHighValueProduct === true}
                className='!mr-0'
              />
              {campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove ? (
                <Badge
                  type={BadgeType.primary}
                  text={`exclusive`}
                  size={BadgeSize.small}
                  className='!mr-0'
                />
              ) : (
                campaign.isPremium && (
                  <Badge
                    // type={BadgeType.secondary}
                    type={BadgeType.amber}
                    text={`premium`}
                    size={BadgeSize.small}
                    className='!mr-0'
                  />
                )
              )}
            </div>
          </div>
        </span>
      </td>

      <td
        className={classNames(
          'whitespace-nowrap px-3 py-4 text-sm text-gray-500',
          campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove &&
            'bg-indigo-50/40',
        )}
      >
        <div>
          {getTotalCreatorEarningsText()}{' '}
          {campaign.product.type === ProductType.physical
            ? ' + free product'
            : ' + digital product'}
        </div>
        {campaign.hooks?.length > 0 && (
          <div className='mt-1 mb-0'>
            <Badge
              type={BadgeType.indigo}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='hooks-badge'
                  content={`${campaign.hooks?.length} variation${campaign.hooks?.length > 1 ? 's' : ''} required`}
                  tooltipText='Each variation is an altered version of the original video, featuring a unique hook or call-to-action. The duration of each variation is up to 5 seconds.'
                />
              }
              size={BadgeSize.medium}
              className='!mr-0'
            />
          </div>
        )}
        {campaign.addons?.length > 0 && (
          <div className='mt-1 mb-0'>
            <Badge
              type={BadgeType.purple}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='addons-badge'
                  content={`${campaign.addons?.length} add-on${campaign.addons?.length > 1 ? 's' : ''} required`}
                  tooltipText={mapAddonsToTooltipTextForCreators(
                    campaign.addons,
                  )}
                />
              }
              size={BadgeSize.medium}
              className='!mr-0'
            />
          </div>
        )}
      </td>

      <td
        className={classNames(
          'whitespace-nowrap px-3 py-4 text-sm text-gray-500',
          campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove &&
            'bg-indigo-50/40',
        )}
      >
        <div className='truncate mb-0 flex flex-shrink-0'>
          {campaign.brand.contractType ===
            ContentContractType.fullAssignment && (
            <Badge
              // type={BadgeType.primary}
              type={BadgeType.orange}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='custom-contract-badge'
                  content='Custom Contract'
                  tooltipText='This campaign requires a custom contract and content ownership transfer.'
                  // iconColor='text-white'
                  iconColor='text-orange-700'
                />
              }
              size={BadgeSize.medium}
            />
          )}
          <Badge
            type={BadgeType.gray}
            iconPosition='left'
            text={mapAspectRatioToText(campaign.aspectRatio)}
            size={BadgeSize.medium}
          />
          <Badge
            type={BadgeType.gray}
            iconPosition='left'
            text={videoDurationLocalizedString(campaign.videoDuration)}
            size={BadgeSize.medium}
          />
        </div>
        <div className='truncate mt-1 mb-0 flex flex-shrink-0'>
          <Badge
            type={BadgeType.gray}
            iconPosition='left'
            text={videoTypeLocalizedString(campaign.videoType)}
            size={BadgeSize.medium}
          />
          <Badge
            type={BadgeType.gray}
            iconPosition='left'
            text={modelTypeLocalizedString(campaign.modelType)}
            size={BadgeSize.medium}
          />
          {campaign.voiceType && (
            <Badge
              type={BadgeType.gray}
              iconPosition='left'
              text={voiceTypeLocalizedString(campaign.voiceType)}
              size={BadgeSize.medium}
            />
          )}
        </div>
      </td>

      <td
        className={classNames(
          'relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-2',
          campaign.brand.serviceLevel === BrandServiceLevel.whiteGlove &&
            'bg-indigo-50/40',
        )}
      >
        <Button
          type='button'
          colour={ButtonColor.secondary}
          onClick={() => setIsCampaignSliderOpen(true)}
          size={ButtonSize.medium}
          icon={
            <Icon
              type={IconType.layers}
              className='h-4 w-4 text-white'
              aria-hidden='true'
            />
          }
          iconPosition='left'
          tooltip='View campaign details'
        />
        <Button
          title={
            pathname ===
            CreatorRoutes.invitations.replace(':id', `${creator.id}`)
              ? 'Apply'
              : 'Apply'
          }
          type='button'
          colour={ButtonColor.primary}
          onClick={() => setIsApplyToCampaignModalOpen(true)}
        />
      </td>
    </>
  )
}

export { CampaignCard }
